/**
 * Storage 工具 - 支持 ID 后缀的 localStorage 和 sessionStorage
 */

const hashKeys = [
  'vptyp',
  'vplocl',
  'JKlp_pxl_w',
  'dd_channel',
  'session',
  'web_mail_uid',
  'vpses',
  'provider_order_id',
  'payermax_package_id',
  'cgpsty',
]

// 字符串加密函数
function hash8Sync(str) {
  let hash = 0x811c9dc5 // FNV offset basis
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i)
    hash = (hash * 0x01000193) >>> 0 // FNV prime
  }
  return hash.toString(16).padStart(8, '0')
}

/** 同 origin 下所有游戏/路径共用，不加 VUE_APP_GAME_ID 后缀 */
export const VIP_MALL_LANG_CODE_KEY = 'vipMallLangCode'

function legacyVipMallLangKey() {
  const id = process.env.VUE_APP_GAME_ID
  return id ? `${VIP_MALL_LANG_CODE_KEY}_${id}` : null
}

export const getStorageKey = (key, id = process.env.VUE_APP_GAME_ID) => {
  if (key === VIP_MALL_LANG_CODE_KEY) {
    return key
  }
  // 兼容 Kingshot，localStorage 和 sessionStorage 不加 game_id 后缀
  if (id === '20121' && key !== 'session') {
    return key
  }
  return id ? `${key}_${id}` : key
}

// 创建自定义 Storage 对象
export const createCustomLocalStorage = (id = '') => {
  return {
    setItem: (key, value) => {
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
      if (key === VIP_MALL_LANG_CODE_KEY) {
        localStorage.setItem(VIP_MALL_LANG_CODE_KEY, stringValue)
        const legacy = legacyVipMallLangKey()
        if (legacy) localStorage.removeItem(legacy)
        return
      }

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        console.log('加密localset', key, newKey, keyWithId)
        localStorage.setItem(keyWithId, stringValue)
        return
      }

      const keyWithId = getStorageKey(key)
      localStorage.setItem(keyWithId, stringValue)
    },

    getItem: (key, defaultValue = null) => {
      if (key === VIP_MALL_LANG_CODE_KEY) {
        const value = localStorage.getItem(VIP_MALL_LANG_CODE_KEY)
        return value === null ? defaultValue : value
      }

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        console.log('加密localget', key, newKey, keyWithId)
        return localStorage.getItem(keyWithId)
      }

      const keyWithId = getStorageKey(key)
      const value = localStorage.getItem(keyWithId)

      if (value === null) return defaultValue

      return value
    },

    removeItem: (key) => {
      if (key === VIP_MALL_LANG_CODE_KEY) {
        localStorage.removeItem(VIP_MALL_LANG_CODE_KEY)
        const legacy = legacyVipMallLangKey()
        if (legacy) localStorage.removeItem(legacy)
        return
      }

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        localStorage.removeItem(keyWithId)
        console.log('加密localremove', key, newKey, keyWithId)
        return
      }

      localStorage.removeItem(getStorageKey(key))
    },

    clear: () => {
      const keys = Object.keys(localStorage)
      const idSuffix = id ? `_${id}` : ''
      keys.forEach((key) => {
        if (idSuffix === '' || key.endsWith(idSuffix)) {
          localStorage.removeItem(key)
        }
      })
    },
  }
}

export const createCustomSessionStorage = (id = '') => {
  return {
    setItem: (key, value) => {
      const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
      if (key === VIP_MALL_LANG_CODE_KEY) {
        sessionStorage.setItem(VIP_MALL_LANG_CODE_KEY, stringValue)
        const legacy = legacyVipMallLangKey()
        if (legacy) sessionStorage.removeItem(legacy)
        return
      }

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        sessionStorage.setItem(keyWithId, stringValue)
        console.log('sessionset', key, newKey, keyWithId)
        return
      }

      const keyWithId = getStorageKey(key)
      sessionStorage.setItem(keyWithId, stringValue)
    },

    getItem: (key, defaultValue = null) => {
      if (key === VIP_MALL_LANG_CODE_KEY) {
        const value = sessionStorage.getItem(VIP_MALL_LANG_CODE_KEY)
        return value === null ? defaultValue : value
      }
      const keyWithId = getStorageKey(key)
      const value = sessionStorage.getItem(keyWithId)

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        console.log('加密sessionget', key, newKey, keyWithId)
        return sessionStorage.getItem(keyWithId)
      }

      if (value === null) return defaultValue

      return value
    },

    removeItem: (key) => {
      if (key === VIP_MALL_LANG_CODE_KEY) {
        sessionStorage.removeItem(VIP_MALL_LANG_CODE_KEY)
        const legacy = legacyVipMallLangKey()
        // 移除老数据
        if (legacy) sessionStorage.removeItem(legacy)
        return
      }

      if (hashKeys.includes(key)) {
        const newKey = hash8Sync(key)
        const keyWithId = getStorageKey(newKey)
        sessionStorage.removeItem(keyWithId)
        console.log('加密sessionremove', key, newKey, keyWithId)
        return
      }

      sessionStorage.removeItem(getStorageKey(key))
    },

    clear: () => {
      const keys = Object.keys(sessionStorage)
      const idSuffix = id ? `_${id}` : ''
      keys.forEach((key) => {
        if (idSuffix === '' || key.endsWith(idSuffix)) {
          sessionStorage.removeItem(key)
        }
      })
    },
  }
}

// 统一级别 Cookie 同意存储（不加 game_id 后缀，所有游戏共用）
const UNIFIED_COOKIE_KEYS = ['acceptis', 'acceptAll']

//  统一级别 Cookie 同意存储（不加 game_id 后缀，所有游戏共用）
export const unifiedCookieStorage = {
  setItem(key, value) {
    if (!UNIFIED_COOKIE_KEYS.includes(key)) return
    const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value)
    localStorage.setItem(key, stringValue)
  },
  getItem(key, defaultValue = null) {
    if (!UNIFIED_COOKIE_KEYS.includes(key)) return defaultValue
    const value = localStorage.getItem(key)
    return value === null ? defaultValue : value
  },
  removeItem(key) {
    if (UNIFIED_COOKIE_KEYS.includes(key)) {
      localStorage.removeItem(key)
    }
  },
}

/**
 * 将各游戏单独记录的 cookie 同意迁移为统一记录：刷数据
 * 1. 若已有统一记录则直接返回
 * 2. 遍历所有游戏储存的同意情况，取权限最高者
 * 3. 写入统一 cookie 同意信息，并删除各游戏同意数据
 */
export function migrateCookieConsentToUnified() {
  // 线上是否有acceptis
  const hasUnifiedAccept = unifiedCookieStorage.getItem('acceptis')
  // 线上是否有acceptAll
  const hasUnifiedAcceptAll = unifiedCookieStorage.getItem('acceptAll')
  if (hasUnifiedAccept || hasUnifiedAcceptAll) {
    return
  }

  let mergedAccept = false
  let mergedAcceptAll = false
  const keysToRemove = []

  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key) continue
    if (key.startsWith('acceptis_')) {
      const val = localStorage.getItem(key)
      if (val && val !== 'false' && val !== '0') mergedAccept = true
      keysToRemove.push(key)
    } else if (key.startsWith('acceptAll_')) {
      const val = localStorage.getItem(key)
      if (val === '1') mergedAcceptAll = true
      keysToRemove.push(key)
    }
  }

  if (mergedAccept || mergedAcceptAll) {
    unifiedCookieStorage.setItem('acceptis', true)
  }
  if (mergedAcceptAll) {
    unifiedCookieStorage.setItem('acceptAll', '1')
  } else if (mergedAccept) {
    unifiedCookieStorage.setItem('acceptAll', '0')
  }

  keysToRemove.forEach((k) => localStorage.removeItem(k))
}

// 默认实例
export const customLocalStorage = createCustomLocalStorage(process.env.VUE_APP_GAME_ID)
export const customSessionStorage = createCustomSessionStorage(process.env.VUE_APP_GAME_ID)
